import { fromArrayBuffer } from 'numpy-parser';
import labelsNames from '../data/labels_names.json';

let gtClassData, predClassData, gtRemainingTimeData, predRemainingTimesData;
let videoLength, anticipationTimeSteps, numClasses;

const loadNpy = async (url) => {
  console.log(`Attempting to load NPY file from: ${url}`);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const buffer = await response.arrayBuffer();

    const { shape, data, dtype } = fromArrayBuffer(buffer);

    console.log(`Successfully loaded NPY file from: ${url}`);
    console.log(`Shape: ${shape}, Data type: ${dtype}`);
    console.log(`Sample data: ${data.slice(0, 10)}`);

    return { shape, data, dtype };
  } catch (error) {
    console.error(`Failed to load NPY file from: ${url}`, error);
    throw error;
  }
};

export const loadAllData = async (videoId, setLoadingProgress) => {
  try {
    console.log("Starting to load all data...");
    setLoadingProgress(0);

    console.log("Labels names:", labelsNames);
    setLoadingProgress(20);

    // gt_classification.npy with shape: (video_length, 18+1)
    console.log(`Loading ground truth classification data for video ${videoId}...`);
    const gtClassificationModule = await import(`../data/c80/video${videoId}/gt_classification_vid${videoId}.npy`);
    gtClassData = await loadNpy(gtClassificationModule.default);
    console.log("gt_classification data loaded");
    setLoadingProgress(40);

    // pred_classifications.npy with shape: (video_length, 18+1)
    console.log(`Loading future predictions data for video ${videoId}...`);
    const predClassificationsModule = await import(`../data/c80/video${videoId}/pred_classification_vid${videoId}.npy`);
    predClassData = await loadNpy(predClassificationsModule.default);
    console.log("pred_classifications data loaded");
    setLoadingProgress(60);

    // gt_remaining_time.npy
    console.log(`Loading ground truth remaining times data for video ${videoId}...`);
    const gtRemainingTimeModule = await import(`../data/c80/video${videoId}/gt_remaining_time_vid${videoId}_h18.npy`);
    gtRemainingTimeData = await loadNpy(gtRemainingTimeModule.default);
    console.log("Ground truth remaining times data loaded");
    setLoadingProgress(80);

    // pred_remaining_time.npy
    console.log(`Loading predicted remaining times data for video ${videoId}...`);
    const predRemainingTimesModule = await import(`../data/c80/video${videoId}/pred_remaining_time_vid${videoId}_h18.npy`);
    predRemainingTimesData = await loadNpy(predRemainingTimesModule.default);
    console.log("Predicted remaining times data loaded");
    setLoadingProgress(90);

    videoLength = gtClassData.shape[0];
    anticipationTimeSteps = gtClassData.shape[1] - 1; // Subtract 1 to account for current step
    numClasses = labelsNames.length;
    console.log("All data loaded successfully");
    console.log("Video length:", videoLength);
    console.log("Anticipation time steps:", anticipationTimeSteps);
    console.log("Number of classes:", numClasses);
    console.log("Number of label names:", labelsNames.length);

    setLoadingProgress(100);

    return {
      videoLength,
      numClasses,
    };
  } catch (error) {
    console.error('Error loading data:', error);
    setLoadingProgress(0);
    throw error;
  }
};

export const getDataForTimeIndex = (timeIndex) => {
  console.log("Entering getDataForTimeIndex with timeIndex:", timeIndex);

  if (!gtClassData || !predClassData || !gtRemainingTimeData || !predRemainingTimesData || !labelsNames) {
    console.error("One or more required data sets are undefined:", {
      gtClassData: !!gtClassData,
      predClassData: !!predClassData,
      gtRemainingTimeData: !!gtRemainingTimeData,
      predRemainingTimesData: !!predRemainingTimesData,
      labelsNames: !!labelsNames
    });
    return null;
  }

  const safeGetData = (data, index) => {
    if (data && data.data && index >= 0 && index < data.data.length) {
      return data.data[index];
    }
    console.error(`Invalid data access: ${data ? 'data exists' : 'data is undefined'}, index: ${index}, data length: ${data && data.data ? data.data.length : 'unknown'}`);
    return null;
  };

  // Ground truth current class and future classes
  const gtCurrentClass = safeGetData(gtClassData, timeIndex * 19);
  const gtFutureClasses = Array.from({ length: 18 }, (_, i) => 
    safeGetData(gtClassData, timeIndex * 19 + i + 1)
  ).filter(val => val !== null);

  // Predicted current class and future classes
  const predCurrentClass = safeGetData(predClassData, timeIndex * 19);
  const predFutureClasses = Array.from({ length: 18 }, (_, i) => 
    safeGetData(predClassData, timeIndex * 19 + i + 1)
  ).filter(val => val !== null);

  // Ground truth remaining times
  const gtRemainingTimes = Array.from({ length: numClasses }, (_, i) => 
    safeGetData(gtRemainingTimeData, timeIndex * numClasses + i)
  ).filter(val => val !== null);

  // Predicted remaining times
  const predRemainingTimes = Array.from({ length: numClasses }, (_, i) => 
    safeGetData(predRemainingTimesData, timeIndex * numClasses + i)
  ).filter(val => val !== null);

  return {
    gtCurrentClass: gtCurrentClass !== null ? getActionName(gtCurrentClass) : 'Unknown',
    gtFutureClasses: gtFutureClasses.map(c => c !== null ? getActionName(c) : 'Unknown'),
    predCurrentClass: predCurrentClass !== null ? getActionName(predCurrentClass) : 'Unknown',
    predFutureClasses: predFutureClasses.map(c => c !== null ? getActionName(c) : 'Unknown'),
    gtRemainingTimes,
    predRemainingTimes
  };
};

export const getActionName = (classIndex) => {
  const name = labelsNames[classIndex] || `Unknown Action (${classIndex})`;
  console.log(`Class index ${classIndex} maps to name: ${name}`);
  return name;
};

export const getVideoLength = () => videoLength;